import type { FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { LoaderFunction } from 'react-router-dom';
import { Navigate, useLoaderData, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import FormErrors from '../../components/FormErrors';
import AuthLayout from '../../layouts/AuthLayout';
import { getPasswordReset, resetPassword } from '../../lib/allauth';
import type { AllauthResponse } from '../../types/allauth';
import { makePasswordMatchValidator } from './components/passwordValidation';

// eslint-disable-next-line react-refresh/only-export-components -- route loader co-located with page
export const resetPasswordByLinkLoader: LoaderFunction = async ({ params }) => {
  const { key } = params;
  const resp = await getPasswordReset(key);
  return { resetKey: key, resetKeyResponse: resp };
};

interface ResetPasswordFormProps {
  setPassword1: (value: string) => void;
  setPassword2: (value: string) => void;
  password1: string;
  password2: string;
  response: { fetching: boolean; content: AllauthResponse | null };
  handleResetPassword: () => void;
}

function ResetPasswordForm({
  setPassword1,
  setPassword2,
  password1,
  password2,
  response,
  handleResetPassword,
}: ResetPasswordFormProps) {
  const { touchAll, isValid, scrollToField, errorSummary } = useFormContext();

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid) {
        if (errorSummary.length > 0) {
          scrollToField(errorSummary[0].name);
        }
        return;
      }
      handleResetPassword();
    },
    [touchAll, isValid, errorSummary, scrollToField, handleResetPassword]
  );

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
          Create new password
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-400'>
          Enter your new password below.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={onSubmit} noValidate className='flex flex-col gap-6'>
        <FormErrors errors={response.content?.errors} />

        <div className='flex flex-col gap-5'>
          <FormField
            name='password'
            label='New password'
            isRequired
            hint='Must be at least 8 characters.'
          >
            {({ inputId, labelId, isInvalid, showError }) => (
              <Input
                id={inputId}
                aria-labelledby={labelId}
                type='password'
                placeholder='Enter your new password'
                autoComplete='new-password'
                isRequired
                isInvalid={isInvalid}
                showError={showError}
                onChange={setPassword1}
                value={password1}
                name='password'
              />
            )}
          </FormField>
          <FormErrors param='password' errors={response.content?.errors} />

          <FormField name='password2' label='Confirm new password' isRequired>
            {({ inputId, labelId, isInvalid, showError }) => (
              <Input
                id={inputId}
                aria-labelledby={labelId}
                type='password'
                placeholder='Confirm your new password'
                isRequired
                isInvalid={isInvalid}
                showError={showError}
                onChange={setPassword2}
                value={password2}
                name='password2'
              />
            )}
          </FormField>
          <FormErrors param='password2' errors={response.content?.errors} />
        </div>

        <Button
          type='submit'
          isLoading={response.fetching}
          isDisabled={response.fetching}
          showTextWhileLoading={true}
          className='w-full'
        >
          {response.fetching ? 'Resetting password…' : 'Reset password'}
        </Button>
      </form>
    </div>
  );
}

function ResetPassword({
  resetKey,
  resetKeyResponse,
}: {
  resetKey: string;
  resetKeyResponse: AllauthResponse;
}) {
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');

  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });

  const fields = useMemo(
    () => ({
      password: {
        required: true,
        validate: (v: unknown): string | undefined => {
          if (!v) {
            return undefined;
          }
          return String(v).length >= 8
            ? undefined
            : 'Must be at least 8 characters';
        },
      },
      password2: {
        required: true,
        validate: makePasswordMatchValidator(password1),
      },
    }),
    [password1]
  );

  const values = useMemo(
    () => ({ password: password1, password2: password2 }),
    [password1, password2]
  );

  const handleResetPassword = useCallback(() => {
    setResponse(r => ({ ...r, fetching: true }));
    resetPassword({ key: resetKey, password: password1 })
      .then(resp => {
        setResponse(r => ({ ...r, content: resp }));
      })
      .catch(e => {
        console.error(e);
        toast.error(sanitizeError(e));
      })
      .then(() => {
        setResponse(r => ({ ...r, fetching: false }));
      });
  }, [resetKey, password1]);

  if ([200, 401].includes(response.content?.status as number)) {
    return <Navigate to='/account/login' />;
  }
  let body;
  const keyError = response.content?.errors?.filter(e => e.param === 'key');
  if (resetKeyResponse.status !== 200) {
    body = (
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Reset password
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            There was a problem with your reset link.
          </p>
        </div>
        <FormErrors param='key' errors={resetKeyResponse.errors} />
      </div>
    );
  } else if (keyError && keyError.length > 0) {
    body = (
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Reset password
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            There was a problem with your reset link.
          </p>
        </div>
        <FormErrors param='key' errors={response.content?.errors} />
      </div>
    );
  } else {
    body = (
      <FormProvider fields={fields} values={values}>
        <ResetPasswordForm
          setPassword1={setPassword1}
          setPassword2={setPassword2}
          password1={password1}
          password2={password2}
          response={response}
          handleResetPassword={handleResetPassword}
        />
      </FormProvider>
    );
  }

  return <AuthLayout title='Reset Password'>{body}</AuthLayout>;
}

export function ResetPasswordByLink() {
  const { resetKey, resetKeyResponse } = useLoaderData();
  return (
    <ResetPassword resetKey={resetKey} resetKeyResponse={resetKeyResponse} />
  );
}

export function ResetPasswordByCode() {
  const { state } = useLocation();
  if (!state || !state.resetKey || !state.resetKeyResponse) {
    return <Navigate to='/account/password/reset' />;
  }
  return (
    <ResetPassword
      resetKey={state.resetKey}
      resetKeyResponse={state.resetKeyResponse}
    />
  );
}
